import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

export interface PortfolioHubTab {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: string;
}

interface PortfolioHubLayoutProps {
  title: string;
  description: string;
  segmentLabel: string;
  segmentTone?: 'blue' | 'emerald' | 'violet';
  tabs: PortfolioHubTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  children: React.ReactNode;
  headerAside?: React.ReactNode;
}

const segmentToneClasses: Record<NonNullable<PortfolioHubLayoutProps['segmentTone']>, string> = {
  blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  violet: 'bg-violet-500/10 border-violet-500/20 text-violet-400',
};

export const PortfolioHubLayout: React.FC<PortfolioHubLayoutProps> = ({
  title,
  description,
  segmentLabel,
  segmentTone = 'blue',
  tabs,
  activeTab,
  onTabChange,
  children,
  headerAside,
}) => {
  return (
    <div className="min-h-[calc(100vh-64px)] bg-transparent">
      <div className="bg-gradient-to-b from-slate-900/60 via-slate-900/20 to-transparent px-8 pt-10">
        <div className="mx-auto max-w-[1800px]">
          <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-4xl">
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <h1 className="text-3xl font-bold tracking-tight text-white">{title}</h1>
                <span className={cn(
                  'inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em]',
                  segmentToneClasses[segmentTone],
                )}>
                  {segmentLabel}
                </span>
              </div>
              <p className="max-w-3xl text-sm font-medium leading-6 text-slate-400">{description}</p>
            </div>
            {headerAside ? (
              <div className="flex shrink-0 items-center">{headerAside}</div>
            ) : null}
          </div>

          <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-1 backdrop-blur-xl">
            <div className="flex flex-wrap gap-1">
              {tabs.map((tab) => {
                const isActive = tab.id === activeTab;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => onTabChange(tab.id)}
                    className={cn(
                      'group relative flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200',
                      isActive
                        ? 'bg-slate-800 text-white shadow-[0_12px_32px_rgba(15,23,42,0.35)]'
                        : 'text-slate-400 hover:bg-white/5 hover:text-slate-200',
                    )}
                  >
                    <span className={cn(
                      'transition-transform duration-200 group-hover:scale-110',
                      isActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300',
                    )}>
                      {tab.icon}
                    </span>
                    <span>{tab.label}</span>
                    {tab.badge ? (
                      <span
                        className={cn(
                          'rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em]',
                          isActive ? 'bg-blue-500/20 text-blue-300' : 'bg-white/5 text-slate-500',
                        )}
                      >
                        {tab.badge}
                      </span>
                    ) : null}
                    {isActive ? (
                      <motion.div
                        layoutId="portfolio-hub-tab-indicator"
                        className="absolute inset-x-4 bottom-1 h-0.5 rounded-full bg-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.55)]"
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1800px] pb-8">{children}</div>
    </div>
  );
};

export default PortfolioHubLayout;
